import React, { useMemo } from "react";
import "../../css/keyword.scss";
import { useNavigate } from "react-router-dom";

import seo_diff from "../../Assets/keyword/seo-diff.svg";
import search_volume from "../../Assets/keyword/search-volume.svg";
import cost_per_click from "../../Assets/keyword/cost-per-click.svg";
import searchIntent from "../../Assets/keyword/search-intent.png";
import averageImpres from "../../Assets/keyword/average-imp.svg";
import competiIcon from "../../Assets/keyword/compet.svg";
import avgDalClk from "../../Assets/keyword/avg-dai-clk.svg";
import avgBackIcon from "../../Assets/keyword/avg-back.svg";
import domainRankIcon from "../../Assets/keyword/domain-rank.svg";
import CompLevelImg from "../../Assets/keyword/complevel.svg";

import VerticalLinearStepper from "../../share/components/vertical-steper";
import ToolTip from "../../share/components/tooltip";
import { ZustandStore } from "../../../store/zustand-store";
import KeywordDashboardTable from "../../share/tables/KeywordDashboardTable";
import { monthNameArr } from "../../share/upDater/constant";
import KeywordDataProvider from "./KeywordDataProvider";
import FallbackLoader from "../../share/components/FallbackLoader";
import LastCheck from "../Search-History/LastCheck";
import { useTranslation } from "react-i18next";
import ChartWrapper from "../../share/d3-charts/ChartWrapper";
import LineChart from "../../share/d3-charts/LineChart";

const KeywordCard = ({ data }) => {
  return (
    <div className="col-12 col-sm-6 col-lg-4 col-xl-3">
      <div className={"keyword-card-sm"}>
        <div className="d-flex keyword-card-header">
          <h6 className="mb-1">
            {data?.title}
            <ToolTip title={data?.toolkit} />
          </h6>
          <h1 className="mb-0">{data?.value?.toLocaleString() || 0}</h1>
        </div>
        <img src={data?.img} alt={data?.title} />
      </div>
    </div>
  );
};

const KeywordOverview = () => {
  const {
    keyword_research_data,
    keyword_ques_based_data,
    keyword_research_ideas_data,
  } = ZustandStore((state) => state);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // search volume chart and  competition_level
  const valume_chart_data = useMemo(() => {
    const months = monthNameArr;
    const keyword_data = keyword_research_data?.data;

    const labels =
      keyword_data?.seed_keyword_data?.keyword_info?.monthly_searches?.map(
        (res) => `${months[res.month - 1]} ${res.year}`
      ) || [];
    const data =
      keyword_data?.seed_keyword_data?.keyword_info?.monthly_searches?.map(
        (res) => res.search_volume
      ) || [];
    const main_intent =
      keyword_data?.seed_keyword_data?.search_intent_info?.main_intent || "N/A";
    const comp_level =
      keyword_data?.seed_keyword_data?.keyword_info?.competition_level || 0;
    const levels = { LOW: 1, MEDIUM: 2, HIGH: 3 };
    const competition_level = levels[comp_level] ?? 0;
    const domainName = sessionStorage.getItem("KEYWORD_DOMAIN_NAME");
    const chart_title = t("keyword_research.keyword_overview.search_volume");

    if (labels?.length === 0) {
      return {
        chart: {
          title: chart_title,
        },
        chart_data: [],
      };
    } else {
      return {
        competitionLevel: competition_level,
        main_intent,
        chart: {
          title: chart_title,
        },
        chart_data: {
          labels: labels?.reverse(),
          datasets: [
            {
              label: domainName || chart_title,
              data: data?.reverse(),
              borderColor: "#6366F1",
              pointRadius: 0,
              pointHoverRadius: 3,
              pointBorderColor: "#6366F1",
              pointBackgroundColor: "#6366F1",
            },
          ],
        },
      };
    }
  }, [keyword_research_data, t]);

  // keyword large card overview
  const card_overview1 = useMemo(() => {
    const volume =
      keyword_research_data?.data?.seed_keyword_data?.keyword_info
        ?.monthly_searches[0]?.search_volume || 0;
    const sd =
      keyword_research_data?.data?.seed_keyword_data?.keyword_properties
        ?.keyword_difficulty || 0;
    const cpc =
      keyword_research_data?.data?.seed_keyword_data?.keyword_info?.cpc || 0;
    return [
      {
        title: t(
          "keyword_research.keyword_overview.data_box.search_volume.title"
        ),
        toolkit: t(
          "keyword_research.keyword_overview.data_box.search_volume.tooltip"
        ),
        img: search_volume,
        value: volume,
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.seo_difficulty.title"
        ),
        toolkit: t(
          "keyword_research.keyword_overview.data_box.seo_difficulty.tooltip"
        ),
        img: seo_diff,
        value: sd,
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.cost_per_click.title"
        ),
        img: cost_per_click,
        value: `$${cpc}`,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.cost_per_click.tooltip"
        ),
      },
    ];
  }, [keyword_research_data, t]);

  // keyword sm card overview
  const card_overview2 = useMemo(() => {
    const avg_imp =
      keyword_research_data?.data?.seed_keyword_data?.impressions_info
        ?.daily_impressions_average || 0;

    const comp =
      keyword_research_data?.data?.seed_keyword_data?.keyword_info
        ?.competition || 0;

    const avg_clk =
      keyword_research_data?.data?.seed_keyword_data?.impressions_info
        ?.daily_clicks_average || 0;

    const avg_back =
      keyword_research_data?.data?.seed_keyword_data?.avg_backlinks_info
        ?.backlinks || 0;

    const avg_rank =
      keyword_research_data?.data?.seed_keyword_data?.avg_backlinks_info
        ?.main_domain_rank || 0;
    return [
      {
        title: t(
          "keyword_research.keyword_overview.data_box.average_daily_impression.title"
        ),
        img: averageImpres,
        value: avg_imp,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.average_daily_impression.tooltip"
        ),
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.competition.title"
        ),
        img: competiIcon,
        value: comp,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.competition.tooltip"
        ),
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.average_daily_clicks.title"
        ),
        img: avgDalClk,
        value: avg_clk,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.average_daily_clicks.tooltip"
        ),
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.average_backlinks.title"
        ),
        img: avgBackIcon,
        value: avg_back,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.average_backlinks.tooltip"
        ),
      },
      {
        title: t(
          "keyword_research.keyword_overview.data_box.average_domain_rank.title"
        ),
        img: domainRankIcon,
        value: avg_rank,
        toolkit: t(
          "keyword_research.keyword_overview.data_box.average_domain_rank.tooltip"
        ),
      },
    ];
  }, [keyword_research_data, t]);

  const handlePageChange = (path) => {
    ZustandStore.setState({
      tag_keywords: [],
      filter_tag: [],
      keyword_tag_data: {
        loaded: false,
        error: false,
        data: [],
      },
    });
    navigate(path);
  };

  const KeywordHeader = useMemo(() => {
    return [
      {
        id: "keyword",
        sort: "keyword",
        key: "keyword",
        tooltip: t("keyword_research.keyword_overview.keywords.tooltip"),
        minWidth: "300px",
        label: t("keyword_research.keyword_overview.keywords.title"),
        align: "left",
      },

      {
        id: "volume",
        key: "volume",
        sort: "volume",
        tooltip: t("keyword_research.keyword_overview.volume.title"),
        minWidth: "110px",
        label: t("keyword_research.keyword_overview.volume.title"),
        align: "center",
      },
    ];
  }, [t]);

  const keywordRowData = useMemo(() => {
    return (
      keyword_research_data?.data?.items?.map((item) => {
        return {
          keyword: item.keyword,
          volume: item?.keyword_info?.monthly_searches[0]?.search_volume || 0,
        };
      }) || []
    );
  }, [keyword_research_data]);

  const keywordIdeasRowData = useMemo(() => {
    return (
      keyword_research_ideas_data.data?.map((item) => {
        return {
          keyword: item.keyword,
          volume: item?.keyword_info?.monthly_searches[0]?.search_volume || 0,
        };
      }) || []
    );
  }, [keyword_research_ideas_data]);

  const QsbKeywordHeader = useMemo(() => {
    return [
      {
        id: "keyword",
        sort: "keyword",
        key: "keyword",
        tooltip: t("keyword_research.keyword_overview.keywords.tooltip"),
        minWidth: "300px",
        label: t("keyword_research.keyword_overview.keywords.title"),
        align: "left",
      },
      {
        id: "search_volume",
        key: "search_volume",
        sort: "search_volume",
        tooltip: t("keyword_research.keyword_overview.volume.title"),
        minWidth: "250px",
        label: t("keyword_research.keyword_overview.volume.title"),
        align: "center",
      },
    ];
  }, [t]);

  const QsbrowData = useMemo(() => {
    return (
      keyword_ques_based_data?.data?.map((item) => {
        return {
          keyword: item.question,
          search_volume: item.search_volume,
        };
      }) || []
    );
  }, [keyword_ques_based_data]);

  return (
    <KeywordDataProvider title={t("keyword_research.keyword_overview.title")}>
      {keyword_research_data?.data.length === 0 ||
      keyword_research_data?.data?.items == null ? (
        <div className="mt-2">
          <LastCheck type="keyword" />
        </div>
      ) : null}
      {keyword_research_data.loading ? (
        <FallbackLoader />
      ) : keyword_research_data?.search ? (
        <>
          <section id="keyresearch" className="keyword-dashboard mt-4">
            <div className="keyword-card-container">
              <div className="keyword-card-cont row g-3">
                {card_overview1?.map((item) => {
                  return (
                    <KeywordCard data={item} key={item?.title} type={"sm"} />
                  );
                })}
                {card_overview2?.map((item) => {
                  return (
                    <KeywordCard data={item} key={item?.title} type={"sm"} />
                  );
                })}
              </div>
            </div>
            <div className="competit-lvl row g-3">
              <div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                {valume_chart_data?.main_intent && (
                  <div className="search-intent">
                    <h1>{valume_chart_data?.main_intent}</h1>
                    <img src={searchIntent} alt="search-intent" />
                  </div>
                )}
              </div>
              <div className="col-12 col-sm-6 col-lg-3 col-xl-2">
                <div className="comp-lvl-stepper" id="competit-lvl">
                  <div className="text">
                    <img src={CompLevelImg} alt="img" />
                  </div>
                  <div className="stepper">
                    <VerticalLinearStepper
                      activeStep={valume_chart_data?.competitionLevel || 0}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-12 col-lg-6 col-xl-8">
                <ChartWrapper chart={valume_chart_data.chart}>
                  <LineChart data={valume_chart_data.chart_data} />
                </ChartWrapper>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-12 col-md-6 col-xl-4">
                <KeywordDashboardTable
                  {...{
                    title: t("keyword_research.keyword_variations.title"),
                    tooltip: t("keyword_research.keyword_variations.tooltip"),
                    header: KeywordHeader,
                    data: keywordRowData,
                    loading: keyword_research_data.loading,
                    handleFullPage: () => {
                      sessionStorage.setItem("KEY_VAR_TAB", 1);
                      handlePageChange("/keyword-research/variations");
                    },
                  }}
                  key={"Keyword Variations"}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-4">
                <KeywordDashboardTable
                  {...{
                    title: t("keyword_research.keyword_ideas.title"),
                    tooltip: t("keyword_research.keyword_ideas.tooltip"),
                    header: KeywordHeader,
                    data: keywordIdeasRowData,
                    loading: keyword_research_ideas_data.loading,
                    handleFullPage: () =>
                      handlePageChange("/keyword-research/ideas"),
                  }}
                  key={"Keyword Ideas"}
                />
              </div>
              <div className="col-12 col-md-12 col-xl-4">
                <KeywordDashboardTable
                  {...{
                    title: t(
                      "keyword_research.keyword_variations.pages.question_based_keyword.title"
                    ),
                    tooltip: t(
                      "keyword_research.keyword_variations.pages.question_based_keyword.tooltip"
                    ),
                    header: QsbKeywordHeader,
                    loading: keyword_ques_based_data.loading,
                    data: QsbrowData,
                    handleFullPage: () => {
                      sessionStorage.setItem("KEY_VAR_TAB", 2);
                      handlePageChange("/keyword-research/variations");
                    },
                  }}
                  key={"Question Based Keywords"}
                />
              </div>
            </div>
          </section>
        </>
      ) : null}
    </KeywordDataProvider>
  );
};

export default KeywordOverview;
